// ============================================================
// «Розыгрыш» случайного победителя прогноза: подсветка бежит по
// участникам, замедляется и останавливается на победителе.
// Один и тот же модуль использует сайт и OBS-оверлей, чтобы кубик
// появлялся примерно одновременно на обоих экранах.
// ============================================================

/** Длительность прокрутки до показа победителя, мс. */
export const DRAW_DURATION_MS = 2200;
/** Минимум шагов подсветки — маленькие лобби тоже должны «крутиться». */
const MIN_STEPS = 14;
/** Каждый следующий шаг длиннее предыдущего — эффект замедления. */
const SLOWDOWN = 1.12;
/** Минимальная пауза между шагами, мс. */
const MIN_TICK_MS = 35;
/** Класс подсветки строки во время прокрутки (стили — у страницы/оверлея). */
export const DRAWING_CLASS = 'is-rolling';

export interface DrawPlan {
    /** Индексы участников по шагам; последний — победитель. */
    order: number[];
    /** Момент подсветки шага от старта, мс. */
    timeouts: number[];
    /** Момент остановки на победителе, мс. */
    total: number;
}

/**
 * План прокрутки: старт случайный, но последний шаг всегда попадает
 * в победителя; шаги замедляются, суммарно ≈ DRAW_DURATION_MS.
 */
export function planDraw(count: number, winner: number): DrawPlan {
    if (count <= 0) return { order: [], timeouts: [], total: 0 };

    const target = ((winner % count) + count) % count;
    // Старт случайный, а число шагов подбираем так, чтобы цикл замкнулся на победителе
    const start = Math.floor(Math.random() * count);
    const dist = (target - start + count) % count;
    const loops = Math.max(1, Math.ceil((MIN_STEPS - dist) / count));
    const steps = dist + 1 + (loops - 1) * count;

    const order: number[] = [];
    for (let k = 0; k < steps; k++) order.push((start + k) % count);

    // Веса 1, 1.12, 1.12² … нормируем на общую длительность
    const weights = order.map((_, k) => Math.pow(SLOWDOWN, k));
    const sum = weights.reduce((a, b) => a + b, 0);
    const timeouts: number[] = [];
    let acc = 0;
    for (const weight of weights) {
        acc += Math.max(MIN_TICK_MS, Math.round((weight * DRAW_DURATION_MS) / sum));
        timeouts.push(acc);
    }
    return { order, timeouts, total: acc };
}

/**
 * Проигрывает план: onTick(индекс) на каждом шаге, onDone() — после
 * остановки на победителе. Возвращает функцию отмены.
 */
export function playDraw(
    plan: DrawPlan,
    onTick: (index: number) => void,
    onDone: () => void
): () => void {
    let cancelled = false;
    const timers: number[] = [];

    plan.order.forEach((index, step) => {
        timers.push(
            window.setTimeout(() => {
                if (!cancelled) onTick(index);
            }, plan.timeouts[step])
        );
    });
    timers.push(
        window.setTimeout(() => {
            if (!cancelled) onDone();
        }, plan.total)
    );

    return () => {
        cancelled = true;
        for (const id of timers) clearTimeout(id);
    };
}

/**
 * Анимация подсветки строк в контейнере (класс DRAWING_CLASS).
 * Вызывать после отрисовки строк без разыгранного победителя.
 */
export function animateDrawRows(
    container: HTMLElement,
    selector: string,
    count: number,
    winner: number,
    onDone: () => void
): () => void {
    const rows = Array.from(container.querySelectorAll<HTMLElement>(selector)).slice(0, count);
    let marked: HTMLElement | null = null;

    const clearMark = (): void => {
        marked?.classList.remove(DRAWING_CLASS);
        marked = null;
    };

    const plan = planDraw(count, winner);
    return playDraw(
        plan,
        (index) => {
            clearMark();
            const row = rows[index];
            if (row) row.classList.add(DRAWING_CLASS);
            marked = row ?? null;
        },
        () => {
            clearMark();
            onDone();
        }
    );
}
